import { ReactNode, useMemo } from "react";
import { Box } from "@mantine/core";
import { ZoneSymbolKind } from "@utils/TestUtils";
import ZoneSymbolShapes, { ZONE_SYMBOL_VIEWBOX_SIZE } from "./ZoneSymbolShapes";
import {
  CrosshairGaps,
  VfaMapExtent,
  VfaMapGeometry,
  extentHeightDegrees,
  extentWidthDegrees,
  lineOutsideGaps,
  placeUnflipped,
  toFlippedViewBox,
} from "./vfaMapGeometry";

export type VfaMapCellContent = "value" | "probabilityTile" | "zoneSymbol";

/** Cap height of a printed value, as a fraction of one cell. */
const VALUE_FONT_SIZE_RATIO = 0.42;
/** Blank margin left around a probability tile so neighbouring tiles read apart. */
const PROBABILITY_TILE_INSET_RATIO = 0.15;
// Patterns with off-lattice points draw every symbol one finest-cell size, as the HFA 24-2C sheet does.
/** Device pixels, matching the 1px border the crosshair used to be drawn with. */
const CROSSHAIR_WIDTH_PX = 1;
/** Gap punched in the crosshair for the foveal threshold, in cells. */
const FOVEAL_GAP_WIDTH_RATIO = 0.9;
const FOVEAL_GAP_HEIGHT_RATIO = 0.65;
/** Edge length of the blind spot triangle, as a fraction of one cell. */
const BLIND_SPOT_MARKER_SIZE_RATIO = 0.35;

export interface VfaMapSvgProps {
  geometry: VfaMapGeometry;
  /** One entry per test location. Omitted by maps whose content is the underlay. */
  values?: Array<string | number>;
  content?: VfaMapCellContent;
  /** `{img}` placeholder path for the `probabilityTile` content mode. */
  probabilityTileBasePath?: string;
  /** Value indexes to leave blank, such as the blind spot on a deviation map. */
  blankValueIndexes?: number[];
  crosshairColor: string;
  fovealThreshold?: number | null;
  /**
   * Drawn behind the locations at the map's own scale — used by the gray scale
   * map to lay its interpolated field under the crosshair.
   */
  underlay?: ReactNode;
  /** Degree coordinate of a recorded blind spot response, if any. */
  blindSpot?: { xDegrees: number; yDegrees: number } | null;
}

/**
 * One visual field map as a single SVG. Locations are placed by their true
 * degrees inside a degree-based view box, so square cells fall out of the
 * geometry instead of resting on a CSS aspect ratio that a text line box can
 * override.
 */
export default function VfaMapSvg(props: VfaMapSvgProps) {
  const { geometry, blankValueIndexes, crosshairColor } = props;
  const values = props.values ?? [];
  const content = props.content ?? "value";
  const extent = geometry.extent;
  const cellSpan = geometry.cellSpanDegrees;
  const blanked = new Set(blankValueIndexes ?? []);
  const pointsInDrawOrder = useMemo(() => pointsLargestCellFirst(geometry), [geometry]);
  const valueFontSize = cellSpan * VALUE_FONT_SIZE_RATIO;
  const hasOffLatticePoints = geometry.points.some(({ offLattice }) => offLattice);
  const finestCellSpan = Math.min(...geometry.points.map(({ cellSpanDegrees }) => cellSpanDegrees));
  const crosshairGaps = content === "value" ? geometry.crosshairGaps : null;

  return (
    <Box w="100%" style={{ aspectRatio: extentWidthDegrees(extent) / extentHeightDegrees(extent) }}>
      <svg
        data-testid="vfa-field-map"
        width="100%"
        height="100%"
        viewBox={toFlippedViewBox(extent)}
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Degrees run bottom-up, so the whole field is drawn y-flipped and
            every child can be positioned straight from its angle coordinate. */}
        <g transform="scale(1,-1)">
          {props.underlay}
          {/* vector-effect does not inherit, so it goes on each line: without
              it the stroke width would be read in degrees, not pixels. */}
          <g stroke={crosshairColor} strokeWidth={CROSSHAIR_WIDTH_PX}>
            {crosshairGaps ? (
              <CrosshairWithGaps extent={extent} gaps={crosshairGaps} />
            ) : (
              <>
                <line
                  data-testid="vfa-field-axis"
                  vectorEffect="non-scaling-stroke"
                  x1={extent.leftDegrees}
                  y1={0}
                  x2={extent.rightDegrees}
                  y2={0}
                />
                <line
                  vectorEffect="non-scaling-stroke"
                  x1={0}
                  y1={extent.bottomDegrees}
                  x2={0}
                  y2={extent.topDegrees}
                />
              </>
            )}
          </g>
          {pointsInDrawOrder.map(
            ({ valueIndex, xDegrees, yDegrees, valueXDegrees, valueYDegrees, cellSpanDegrees }) => {
              if (blanked.has(valueIndex)) return null;
              return (
                <MapCell
                  key={valueIndex}
                  content={content}
                  value={values[valueIndex]}
                  xDegrees={xDegrees}
                  yDegrees={yDegrees}
                  cellSpan={cellSpanDegrees}
                  valueXDegrees={valueXDegrees}
                  valueYDegrees={valueYDegrees}
                  valueFontSize={valueFontSize}
                  probabilityTileSpan={
                    hasOffLatticePoints
                      ? finestCellSpan
                      : cellSpanDegrees * (1 - 2 * PROBABILITY_TILE_INSET_RATIO)
                  }
                  probabilityTileBasePath={props.probabilityTileBasePath}
                />
              );
            },
          )}
          {props.fovealThreshold != null && (
            <FovealThreshold value={props.fovealThreshold} cellSpan={cellSpan} />
          )}
          {props.blindSpot && <BlindSpotMarker {...props.blindSpot} cellSpan={cellSpan} />}
        </g>
      </svg>
    </Box>
  );
}

/** The crosshair with the stretches a pattern's print layout leaves out around its crowded values. */
function CrosshairWithGaps({ extent, gaps }: { extent: VfaMapExtent; gaps: CrosshairGaps }) {
  const horizontal = lineOutsideGaps(extent.leftDegrees, extent.rightDegrees, gaps.horizontal);
  const vertical = lineOutsideGaps(extent.bottomDegrees, extent.topDegrees, gaps.vertical);
  return (
    <>
      {horizontal.map(([from, to], index) => (
        <line
          key={`horizontal-${from}`}
          data-testid={index === 0 ? "vfa-field-axis" : undefined}
          vectorEffect="non-scaling-stroke"
          x1={from}
          y1={0}
          x2={to}
          y2={0}
        />
      ))}
      {vertical.map(([from, to]) => (
        <line
          key={`vertical-${from}`}
          vectorEffect="non-scaling-stroke"
          x1={0}
          y1={from}
          x2={0}
          y2={to}
        />
      ))}
    </>
  );
}

function pointsLargestCellFirst(geometry: VfaMapGeometry) {
  return [...geometry.points].sort((a, b) => b.cellSpanDegrees - a.cellSpanDegrees);
}

interface MapCellProps {
  content: VfaMapCellContent;
  value: string | number;
  xDegrees: number;
  yDegrees: number;
  cellSpan: number;
  valueXDegrees: number;
  valueYDegrees: number;
  valueFontSize: number;
  probabilityTileSpan: number;
  probabilityTileBasePath?: string;
}

function isBlankValue(value: string | number | undefined) {
  return value === "" || value == null;
}

function MapCell({
  content,
  value,
  xDegrees,
  yDegrees,
  cellSpan,
  valueXDegrees,
  valueYDegrees,
  valueFontSize,
  probabilityTileSpan,
  probabilityTileBasePath,
}: MapCellProps) {
  if (isBlankValue(value)) return null;

  if (content === "zoneSymbol") {
    return (
      <g
        transform={`${placeUnflipped(xDegrees, yDegrees, cellSpan / ZONE_SYMBOL_VIEWBOX_SIZE)} translate(${-ZONE_SYMBOL_VIEWBOX_SIZE / 2} ${-ZONE_SYMBOL_VIEWBOX_SIZE / 2})`}
      >
        <ZoneSymbolShapes kind={value as ZoneSymbolKind} />
      </g>
    );
  }

  if (content === "probabilityTile") {
    return (
      <image
        transform={placeUnflipped(xDegrees, yDegrees)}
        x={-probabilityTileSpan / 2}
        y={-probabilityTileSpan / 2}
        width={probabilityTileSpan}
        height={probabilityTileSpan}
        href={probabilityTileBasePath?.replace("{img}", String(value))}
      />
    );
  }

  return (
    <MapLabel xDegrees={valueXDegrees} yDegrees={valueYDegrees} fontSize={valueFontSize}>
      {value}
    </MapLabel>
  );
}

interface MapLabelProps {
  xDegrees: number;
  yDegrees: number;
  fontSize: number;
  fontWeight?: number;
  children: ReactNode;
}

function MapLabel({ xDegrees, yDegrees, fontSize, fontWeight, children }: MapLabelProps) {
  return (
    <text
      transform={placeUnflipped(xDegrees, yDegrees)}
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={fontSize}
      fontWeight={fontWeight}
      fill="currentColor"
    >
      {children}
    </text>
  );
}

/**
 * The foveal threshold reads at the crosshair intersection, so it needs the
 * crosshair painted out behind it. The explicit colour adjust keeps that gap
 * from being dropped as a background when the sheet is rendered to PDF.
 */
function FovealThreshold({ value, cellSpan }: { value: number; cellSpan: number }) {
  const gapWidth = cellSpan * FOVEAL_GAP_WIDTH_RATIO;
  const gapHeight = cellSpan * FOVEAL_GAP_HEIGHT_RATIO;

  return (
    <g data-testid="vfa-crosshair-gap">
      <rect
        x={-gapWidth / 2}
        y={-gapHeight / 2}
        width={gapWidth}
        height={gapHeight}
        fill="var(--mantine-color-body)"
        style={{ WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}
      />
      <g data-testid="vfa-foveal-threshold" aria-label={`Foveal threshold: ${value} dB`}>
        <MapLabel
          xDegrees={0}
          yDegrees={0}
          fontSize={cellSpan * VALUE_FONT_SIZE_RATIO}
          fontWeight={600}
        >
          {value}
        </MapLabel>
      </g>
    </g>
  );
}

interface BlindSpotMarkerProps {
  xDegrees: number;
  yDegrees: number;
  cellSpan: number;
}

/** Triangle marking the location a recorded blind spot response falls at. */
function BlindSpotMarker({ xDegrees, yDegrees, cellSpan }: BlindSpotMarkerProps) {
  const size = cellSpan * BLIND_SPOT_MARKER_SIZE_RATIO;
  const half = size / 2;

  return (
    <g
      transform={placeUnflipped(xDegrees, yDegrees)}
      data-testid="vfa-blind-spot-location"
      role="img"
      aria-label="Blind spot location"
    >
      <polygon points={`0,${-half} ${half},${half} ${-half},${half}`} fill="currentColor" />
    </g>
  );
}
